import React, { useCallback } from 'react';
import { Button } from '@vp/swan';
import {
  getProductConfiguration,
  getSpecificationItemsAsync,
  Validation,
  ValidationManager,
  ValidationState,
} from '@vp/upload-and-print-validator';
import { useClientValidation } from 'src/clientValidation';
import { useFeatureGate } from 'src/debug/feature-gate/FeatureGatesContext';
import { useErrorContext } from 'src/hooks/ErrorContextHooks';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useTrackingContext } from 'src/hooks/TrackingContextHooks';
import { useUploadContext } from 'src/hooks/UploadContextHooks';
import { PricingContext, usePricingContext } from 'src/hooks/usePricingContext';
import { useValidationContext } from 'src/hooks/ValidationContextHooks';
import { abortControllerManager } from 'src/managers/AbortControllerManager';
import { ProductInformation } from 'src/providers/ProductContextProvider';
import { TrackingConfiguration } from 'src/providers/TrackingContextProvider';
import { processAndValidateUploadFromClient } from 'src/services/UploadAndPrintClientService';
import { processAndValidateUpload, Upload } from 'src/services/UploadAndPrintService';
import { getAuthHeader } from 'src/utils/authHeaderUtil';
import { currentCulture } from 'src/utils/cultureUtil';
import { filterValidationToDisplay } from 'src/utils/filterValidationToDisplay';
import { text } from 'src/utils/localizationUtil';
import {
  filterRecommendedProductOptionsToJustProductPageOptions,
  pickRecommendedOptions,
  updateProductOptionQueryParameters,
} from 'src/utils/productOptionUtils';
import { getTenant } from 'src/utils/tenantUtil';
import { createTrackingDataForProduct, TrackingDataForProduct } from 'src/utils/trackingUtil';
import { FEATURE_GATE_UPLOAD_AND_PRINT_PROGRESSIVE_VALIDATION } from '../../debug/feature-gate/gates';
import { AnalyticsEventData, sendEventTracking } from '../Analytics';

/**
 * An upload is split apart here, because we need to call this from within a useEffect,
 * and passing in the full upload requires the useEffect to have a dependency on Upload, which
 * will change to a new object reference as this function fires (and then re-trigger the useEffect
 * infinitely).  This way the useEffect can have a dependency on the strings that make up an upload,
 * and use string equality comparison to determine if the upload changes, rather than reference equality comparison
 * to an object
 */
export async function sendToPrepress(
  validationManager: ValidationManager | null,
  uploadFileUri: string | undefined,
  uploadFileName: string | undefined,
  uploadThumbnailUri: string | undefined,
  setUpload: (newUploads: Upload | null) => void,
  setValidations: (newValidations: Validation[] | null) => void,
  setIsValidating: (isValidating: boolean) => void,
  productInformation: ProductInformation,
  trackingConfiguration: TrackingConfiguration | null,
  trackingDataForProduct: TrackingDataForProduct,
  isVat: boolean,
  fixedPageSizeWarning: (status: boolean) => void,
  isClientValidationEnabled = false
): Promise<void> {
  if (!uploadFileUri) {
    throw Error('Upload is null or undefined');
  }

  setIsValidating(true);
  const abortSignal = abortControllerManager.getSignal();

  if (validationManager && isClientValidationEnabled) {
    const completedOptions = await getProductConfiguration(
      {
        productId: productInformation.productKey,
        version: productInformation.productVersion,
        productOptions: productInformation.selectedOptions,
        market: currentCulture.split('-')[1],
        tenant: getTenant(),
      },
      getAuthHeader()
    );
    const result = await getSpecificationItemsAsync(
      {
        productKey: productInformation.productKey,
        productVersion: productInformation.productVersion,
        optionSelections: completedOptions,
        culture: currentCulture.toLowerCase(),
        tenant: getTenant(),
      },
      { ...getAuthHeader() }
    );

    validationManager.updateConfiguration(productInformation.selectedOptions, result);
    validationManager.runProductValidationOnly();

    const [processedUploadUri, processUploadResponse] = await processAndValidateUploadFromClient(
      validationManager,
      uploadFileUri,
      productInformation,
      trackingConfiguration,
      trackingDataForProduct,
      isVat,
      fixedPageSizeWarning,
      abortSignal
    );

    const newUpload: Upload = {
      fileUri: uploadFileUri,
      uploadedFileName: uploadFileName,
      thumbnailUrl: uploadThumbnailUri,
      processedUploadUri: processedUploadUri,
      renderingInstructionsUrl: processUploadResponse.renderingInstructionsUrl,
      cimDocUrl: processUploadResponse.cimDocUrl,
    };

    const filteredValidations = filterValidationToDisplay(processUploadResponse.validations);
    setUpload(newUpload);
    setValidations(filteredValidations);
    setIsValidating(false);
  } else {
    const [processedUploadUri, processUploadResponse] = await processAndValidateUpload(
      uploadFileUri,
      productInformation,
      trackingConfiguration,
      trackingDataForProduct,
      isVat,
      fixedPageSizeWarning,
      abortSignal
    );

    const newUpload: Upload = {
      fileUri: uploadFileUri,
      uploadedFileName: uploadFileName,
      thumbnailUrl: uploadThumbnailUri,
      processedUploadUri: processedUploadUri,
      renderingInstructionsUrl: processUploadResponse.renderingInstructionsUrl,
      cimDocUrl: processUploadResponse.cimDocUrl,
    };

    setUpload(newUpload);

    setValidations(processUploadResponse.validations);
    setIsValidating(false);
  }
}

async function saveChanges(
  validationManager: ValidationManager | null,
  upload: Upload | null,
  productInformation: ProductInformation,
  setValidations: (newValidations: Validation[] | null) => void,
  setIsValidating: (isValidating: boolean) => void,
  setProductInformation: (newInfo: ProductInformation) => void,
  setError: (error: unknown) => void,
  setUpload: (upload: Upload | null) => void,
  trackingConfiguration: TrackingConfiguration | null,
  isVat: boolean,
  fixedPageSizeWarning: (status: boolean) => void,
  isClientValidationEnabled = false
) {
  if (productInformation.recommendedProductData && productInformation.recommendedProductData.productOptions) {
    try {
      const productOptionsToAccept = await filterRecommendedProductOptionsToJustProductPageOptions(
        productInformation.productKey,
        productInformation.productVersion,
        productInformation.recommendedProductData.productOptions
      );

      if (productOptionsToAccept) {
        const selectedOptionKeys = Object.keys(productInformation.selectedOptions);
        const pickedRecommendedOption = pickRecommendedOptions(
          productInformation.recommendedProductData?.productOptions,
          selectedOptionKeys
        );

        /**
         * On changing to new recommended option, we reset
         * the current recommended product data to undefined
         */
        const newProductInformation = {
          ...productInformation,
          recommendedProductData: undefined,
          selectedOptions: pickedRecommendedOption,
        };
        setValidations(null);
        updateProductOptionQueryParameters(pickedRecommendedOption);

        setProductInformation(newProductInformation);
        const trackingDataForProduct = createTrackingDataForProduct(newProductInformation);

        sendEventTracking({
          ...trackingConfiguration,
          ...trackingDataForProduct,
          action: 'Fly-Out Clicked',
          label: `${newProductInformation.productName} Fly-out`,
          category: 'Fly-Out',
          eventDetail: 'Save Changes',
          variant: JSON.stringify(newProductInformation.selectedOptions),
        } as AnalyticsEventData);

        await sendToPrepress(
          validationManager,
          upload?.fileUri,
          upload?.uploadedFileName,
          upload?.thumbnailUrl,
          setUpload,
          setValidations,
          setIsValidating,
          {
            ...newProductInformation,
          },
          trackingConfiguration,
          trackingDataForProduct,
          isVat,
          fixedPageSizeWarning,
          isClientValidationEnabled
        );
      } else {
        throw Error(`Tried to save null/undefined recommended product option changes`);
      }
    } catch (e) {
      if ((e as Error).name === 'AbortError') {
        // Silent catch as this is expected
        setUpload(null);
        return;
      }
      console.error('Error updating product options', e);
      setError(e);
      setUpload(null);
    } finally {
      setIsValidating(false);
    }
  }
}

interface SaveChangeButtonProps {
  onSave?: () => void;
  showFullWidth: boolean;
}

export const SaveChangeButton = ({ onSave, showFullWidth }: SaveChangeButtonProps) => {
  const { trackingConfiguration } = useTrackingContext();
  const { productInformation, setProductInformation } = useProductContext();
  const {
    setValidations,
    setIsValidating,
    validationManager,
    validationPlaceholders,
    fixedPageSizeWarning,
  } = useValidationContext();
  const { upload, setUpload } = useUploadContext();
  const { setError } = useErrorContext();
  const pricingContext = usePricingContext() || ({} as PricingContext);
  const { isClientValidationEnabled, setClientValidation } = useClientValidation();
  const { isEnabled: isProgressiveValidationEnabled } = useFeatureGate(
    FEATURE_GATE_UPLOAD_AND_PRINT_PROGRESSIVE_VALIDATION
  );

  const handleOnclick = useCallback(() => {
    if (!isClientValidationEnabled) {
      if (validationPlaceholders) {
        const validationInProgress = validationPlaceholders.map(validationPlaceholder => {
          validationPlaceholder.state = ValidationState.Validating;
          return validationPlaceholder;
        });
        setValidations(validationInProgress);
      }
    }

    saveChanges(
      validationManager,
      upload,
      productInformation,
      setValidations,
      setIsValidating,
      setProductInformation,
      setError,
      setUpload,
      trackingConfiguration,
      pricingContext.vatInclusive,
      fixedPageSizeWarning,
      isClientValidationEnabled
    ).finally(() => {
      setClientValidation(isProgressiveValidationEnabled());
    });
    onSave && onSave();
  }, [
    fixedPageSizeWarning,
    isClientValidationEnabled,
    onSave,
    pricingContext.vatInclusive,
    productInformation,
    setClientValidation,
    setError,
    setIsValidating,
    setProductInformation,
    setUpload,
    setValidations,
    trackingConfiguration,
    upload,
    validationManager,
    validationPlaceholders,
    isProgressiveValidationEnabled,
  ]);

  return (
    <Button skin="primary" onClick={handleOnclick} width={showFullWidth ? 'full-width' : 'standard'}>
      {text('product-option-suggestion.confirm-adjustment')}
    </Button>
  );
};

SaveChangeButton.displayName = 'SaveChangeButton';
